// src/view/RegistrarBitacoras.jsx
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";


const MODULO = "Bitacoras";

/* =================== Auth =================== */
const authConfig = () => {
  const token = localStorage.getItem("token");

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

/* =================== Permisos =================== */
const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const obtenerModulosStorage = () => {
  try {
    const permisosStorage = localStorage.getItem("permisos");
    const usuarioStorage = localStorage.getItem("usuario");
    const userStorage = localStorage.getItem("user");
    const usuariosStorage = localStorage.getItem("usuarios");

    const permisosData = permisosStorage
      ? JSON.parse(permisosStorage)
      : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
          ? JSON.parse(userStorage)
          : usuariosStorage
            ? JSON.parse(usuariosStorage)
            : {};

    const modulos = Array.isArray(permisosData)
      ? permisosData
      : permisosData?.modulos ||
      permisosData?.permisos ||
      permisosData?.usuario?.modulos ||
      permisosData?.usuario?.permisos ||
      permisosData?.user?.modulos ||
      permisosData?.user?.permisos ||
      [];

    return Array.isArray(modulos) ? modulos : [];
  } catch (error) {
    console.error("Error leyendo permisos:", error);
    return [];
  }
};

const obtenerModuloPorNombre = (nombreModulo) => {
  const modulos = obtenerModulosStorage();
  const moduloBuscado = normalizarTexto(nombreModulo);

  return (
    modulos.find((modulo) => {
      const nombre = modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";
      return normalizarTexto(nombre) === moduloBuscado;
    }) || {}
  );
};

const tienePermisoAccion = (modulo, accion) => {
  const accionLower = String(accion || "").toLowerCase();

  const campoPuede =
    accionLower === "crear"
      ? "puedeCrear"
      : accionLower === "leer"
        ? "puedeLeer"
        : accionLower === "actualizar"
          ? "puedeActualizar"
          : accionLower === "eliminar"
            ? "puedeEliminar"
            : "";

  const alias = {
    leer: ["leer", "ver", "listar", "read", "view"],
    crear: ["crear", "create", "add", "registrar"],
    actualizar: ["actualizar", "editar", "update", "edit"],
    eliminar: ["eliminar", "delete", "remove", "borrar"],
  };

  return (
    modulo?.[campoPuede] === true ||
    modulo?.[accionLower] === true ||
    modulo?.[accion] === true ||
    modulo?.acciones?.[accion] === true ||
    modulo?.acciones?.[accionLower] === true ||
    alias[accionLower]?.some((a) => modulo?.[a] === true || modulo?.acciones?.[a] === true) ||
    false
  );
};

/* =================== Helpers UI =================== */
const EmptyState = ({
  title = "Sin registros",
  subtitle = "Aún no has agregado actividades.",
}) => (
  <div className="text-center text-muted py-4">
    <div className="display-6 mb-2">🗒️</div>
    <div className="fw-semibold">{title}</div>
    <div className="small">{subtitle}</div>
  </div>
);

const Pill = ({ children, variant = "secondary" }) => (
  <span className={`badge rounded-pill text-bg-${variant}`}>{children}</span>
);

const safe = (v, fallback = "—") => (v ?? v === 0 ? v : fallback);

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("es-CO");
  } catch {
    return "—";
  }
};

const fullName = (p1, p2, a1, a2) =>
  [p1, p2, a1, a2].filter(Boolean).join(" ").trim() || "—";

const toYMD = (d) => {
  if (!d) return "";
  try {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const da = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${da}`;
  } catch {
    return "";
  }
};

/* =================== Modal: Tabla de Detalles =================== */
const DetalleTableModal = ({
  show,
  onClose,
  actividades,
  onDelete,
  bitacoraIndex,
  puedeEliminar,
}) => {
  if (!show) return null;

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block" }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content shadow-lg border-0">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title mb-1">
                  Detalles —{" "}
                  <span className="text-success">Bitácora {bitacoraIndex + 1}</span>
                </h5>
                <div className="small text-muted">
                  <i className="fa-regular fa-rectangle-list me-1"></i>
                  {actividades.length} actividad{actividades.length !== 1 ? "es" : ""} registrada
                  {actividades.length !== 1 ? "s" : ""}
                </div>
              </div>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body pt-3">
              {actividades.length === 0 ? (
                <EmptyState
                  title="Sin actividades"
                  subtitle="Usa 'Añadir actividad' para crearlas."
                />
              ) : (
                <div className="table-responsive rounded-3 border">
                  <table className="table table-sm table-striped align-middle mb-0">
                    <thead className="table-light">
                      <tr className="text-muted">
                        <th style={{ minWidth: 240 }}>Descripción</th>
                        <th style={{ width: 120 }}>Inicio</th>
                        <th style={{ width: 120 }}>Fin</th>
                        <th style={{ minWidth: 200 }}>Evidencia</th>
                        <th style={{ minWidth: 220 }}>Observaciones</th>
                        <th className="text-end" style={{ width: 90 }}>
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {actividades.map((a, i) => (
                        <tr key={`act-${i}`}>
                          <td className="fw-semibold">
                            {a.descripcion || (
                              <em className="text-muted">Sin descripción</em>
                            )}
                          </td>
                          <td>
                            <Pill variant="light">{a.inicio || "—"}</Pill>
                          </td>
                          <td>
                            <Pill variant="light">{a.fin || "—"}</Pill>
                          </td>
                          <td className="text-truncate" style={{ maxWidth: 280 }}>
                            {a.evidencia || <span className="text-muted">—</span>}
                          </td>
                          <td className="text-truncate" style={{ maxWidth: 280 }}>
                            {a.observaciones || <span className="text-muted">—</span>}
                          </td>
                          <td className="text-end">
                            {puedeEliminar ? (
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => onDelete(i)}
                                title="Eliminar"
                                type="button"
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            ) : (
                              <span className="badge text-bg-light border">Sin permiso</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <td colSpan={6} className="small text-muted py-2 px-3">
                          <i className="fa-regular fa-circle-question me-1"></i>
                          Consejo: agrega evidencia (link/archivo/entregable) para soporte.
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer border-0">
              <button className="btn btn-secondary" onClick={onClose} type="button">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show"></div>
    </>
  );
};

/* =================== Modal: Formulario de Actividad =================== */
const ActividadFormModal = ({ show, onClose, onSave }) => {
  const initial = useMemo(
    () => ({
      descripcion: "",
      inicio: "",
      fin: "",
      evidencia: "",
      observaciones: "",
    }),
    []
  );

  const [form, setForm] = useState(initial);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (show) {
      setForm(initial);
      setTouched(false);
    }
  }, [show, initial]);

  if (!show) return null;

  const isValid = () => !!form.descripcion?.trim() && !!form.inicio && !!form.fin;

  const guardar = () => {
    setTouched(true);
    if (!isValid()) return;

    onSave(form);
    onClose();
  };

  const invalid = (field) => touched && !String(form[field] || "").trim();

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block" }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content shadow-lg border-0">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title mb-1">Añadir actividad</h5>
                <div className="small text-muted">
                  Completa campos requeridos <Pill variant="danger">(*)</Pill>
                </div>
              </div>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body pt-3">
              <div className="mb-3">
                <label className="form-label small fw-semibold">
                  Descripción <span className="text-danger">*</span>
                </label>
                <textarea
                  className={`form-control ${invalid("descripcion") ? "is-invalid" : ""}`}
                  rows={3}
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  placeholder="Describe la actividad realizada…"
                />
                {invalid("descripcion") && (
                  <div className="invalid-feedback">La descripción es obligatoria.</div>
                )}
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">
                    Fecha de Inicio <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${invalid("inicio") ? "is-invalid" : ""}`}
                    value={form.inicio}
                    onChange={(e) => setForm({ ...form, inicio: e.target.value })}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-semibold">
                    Fecha de Fin <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${invalid("fin") ? "is-invalid" : ""}`}
                    value={form.fin}
                    onChange={(e) => setForm({ ...form, fin: e.target.value })}
                  />
                </div>
              </div>

              <div className="mt-3">
                <label className="form-label small fw-semibold">Evidencia</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.evidencia}
                  onChange={(e) => setForm({ ...form, evidencia: e.target.value })}
                  placeholder="Documento, proceso, entregable, enlace, etc…"
                />
              </div>

              <div className="mt-3">
                <label className="form-label small fw-semibold">Observaciones</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.observaciones}
                  onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                  placeholder="Comentarios, dificultades, notas…"
                />
              </div>
            </div>

            <div className="modal-footer border-0">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Cerrar
              </button>

              <button type="button" className="btn btn-success" onClick={guardar}>
                <i className="fa-solid fa-check me-1"></i> Guardar actividad
              </button>
            </div>

          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show"></div>
    </>
  );
};

/* =================== Página principal =================== */
const RegistrarBitacoras = () => {
  const navigate = useNavigate();
  const { etapaId, matriculaId, aprendizId } = useParams();
  const location = useLocation();

  const moduloBitacoras = useMemo(() => obtenerModuloPorNombre(MODULO), []);

  const puedeLeer = useMemo(
    () => tienePermisoAccion(moduloBitacoras, "leer"),
    [moduloBitacoras]
  );

  const puedeCrear = useMemo(
    () => tienePermisoAccion(moduloBitacoras, "crear"),
    [moduloBitacoras]
  );

  const puedeActualizar = useMemo(
    () => tienePermisoAccion(moduloBitacoras, "actualizar"),
    [moduloBitacoras]
  );

  const puedeEliminar = useMemo(
    () => tienePermisoAccion(moduloBitacoras, "eliminar"),
    [moduloBitacoras]
  );

  const isMassive = location.pathname.startsWith(
    "/etapaproductiva/bitacoras/registromasivo/"
  );

  const formRef = useRef(null);

  const [bitacoras, setBitacoras] = useState(Array.from({ length: 12 }, () => []));
  const [bitacoraIds, setBitacoraIds] = useState(Array.from({ length: 12 }, () => null));

  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ etapa: null, aprendiz: null, matricula: null });

  const [query] = useState("");
  const [onlyWithData] = useState(false);

  const [showTable, setShowTable] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [activeIdx, setActiveIdx] = useState(null);

  const [downloadAction, setDownloadAction] = useState(null);
  const downloadLockRef = useRef(false);

  const startDownload = useCallback((action) => {
    if (downloadLockRef.current) return false;

    downloadLockRef.current = true;
    setDownloadAction(action);
    return true;
  }, []);

  const endDownload = useCallback(() => {
    downloadLockRef.current = false;
    setDownloadAction(null);
  }, []);

  const isDownloading = Boolean(downloadAction);

  const [bulkForm, setBulkForm] = useState({
    etapaPracticaObjectId: "",
    numeroDesde: 1,
    numeroHasta: 12,
    estadoBitacora: "BORRADOR",
    soloActivos: true,
    skipWhenNoEtapa: true,
    mergeMode: "replace",
  });
  const [bulkLoading, setBulkLoading] = useState(false);
  const bulkSet = (k, v) => setBulkForm((prev) => ({ ...prev, [k]: v }));

  const tituloPagina = isMassive
    ? "Registro Masivo de Bitácoras"
    : "Bitácoras de Etapa Productiva";

  const grupo = meta?.matricula?.grupoObjectId;
  const programa = grupo?.programaObjectId;

  const nombreEnte =
    meta?.etapa?.nombreEnteconformador ??
    meta?.matricula?.enteObjectId?.nombreEnteconformador ??
    "—";

  const instructor = meta?.etapa?.instructorObjectId || null;
  const jefe = meta?.etapa?.jefeObjectId || null;

  const instructorNombre = instructor
    ? fullName(
      instructor?.nombre1Instructor,
      instructor?.nombre2Instructor,
      instructor?.apellido1Instructor,
      instructor?.apellido2Instructor
    )
    : "—";

  const jefeNombre = jefe
    ? fullName(jefe?.nombre1Jefe, jefe?.nombre2Jefe, jefe?.apellido1Jefe, jefe?.apellido2Jefe)
    : "—";

  const etapaFechaInicio = meta?.etapa?.fechaInicio || meta?.etapa?.fechaInicioEtapaPractica;
  const etapaFechaFin = meta?.etapa?.fechaFin || meta?.etapa?.fechaFinEtapaPractica;

  const nombreAprendiz = () => {
    const a = meta?.aprendiz;
    if (!a) return "—";
    return (
      fullName(
        a?.nombre1Aprendiz,
        a?.nombre2Aprendiz,
        a?.apellido1Aprendiz,
        a?.apellido2Aprendiz
      ) || "—"
    );
  };

  const puedeGestionarLocal = isMassive ? puedeCrear : puedeCrear || puedeActualizar;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  /* =================== Mappers =================== */
  const mapActividadToPayload = (a) => ({
    descripcionActividad: a.descripcion || "",
    fechaInicioActividad: a.inicio || "",
    fechaFinActividad: a.fin || "",
    evidenciaActividad: a.evidencia || "",
    observacionesActividad: a.observaciones || "",
  });

  const buildActivitiesByNumeroFromState = () => {
    const byNumero = {};
    bitacoras.forEach((rows, idx) => {
      if (Array.isArray(rows) && rows.length > 0) {
        byNumero[String(idx + 1)] = rows.map(mapActividadToPayload);
      }
    });
    return byNumero;
  };

  const dedupeActs = (arr) => {
    const seen = new Set();
    return (arr || []).filter((a) => {
      const key = [
        (a.descripcionActividad || "").trim().toLowerCase(),
        a.fechaInicioActividad || "",
        a.fechaFinActividad || "",
        (a.evidenciaActividad || "").trim().toLowerCase(),
        (a.observacionesActividad || "").trim().toLowerCase(),
      ].join("|");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  /* =================== GET principal =================== */
  const getData = useCallback(async () => {
    if (!puedeLeer) {
      setLoading(false);
      setBitacoras(Array.from({ length: 12 }, () => []));
      setBitacoraIds(Array.from({ length: 12 }, () => null));
      setMeta({ etapa: null, aprendiz: null, matricula: null });
      return;
    }

    if (isMassive) {
      setLoading(false);
      return;
    }

    if (!etapaId || !aprendizId || !matriculaId) {
      setLoading(false);
      Swal.fire({
        icon: "warning",
        title: "Faltan parámetros",
        text: "Verifica que la ruta tenga etapaId, aprendizId y matriculaId.",
      });
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.get(
        `/api/bitacoras/listbyrelacion/${etapaId}/${aprendizId}/${matriculaId}`,
        authConfig()
      );

      const next = Array.from({ length: 12 }, () => []);
      const nextIds = Array.from({ length: 12 }, () => null);

      (data?.bitacoras || []).forEach((b) => {
        const idx = Number(b?.numeroBitacora) - 1;
        if (idx >= 0 && idx < 12) {
          const acts = Array.isArray(b?.actividadesBitacora)
            ? b.actividadesBitacora.map((a) => ({
              descripcion: a?.descripcionActividad || "",
              inicio: toYMD(a?.fechaInicioActividad),
              fin: toYMD(a?.fechaFinActividad),
              evidencia: a?.evidenciaActividad || "",
              observaciones: a?.observacionesActividad || "",
            }))
            : [];
          next[idx] = acts;
          nextIds[idx] = b?._id || null;
        }
      });

      setBitacoras(next);
      setBitacoraIds(nextIds);

      setMeta({
        etapa: data?.meta?.etapa || null,
        aprendiz: data?.meta?.aprendiz || null,
        matricula: data?.meta?.matricula || null,
      });
    } catch (err) {
      console.error("Error cargando bitácoras:", err?.response?.data || err?.message);

      if (manejarErrorSesion(err)) return;

      setBitacoras(Array.from({ length: 12 }, () => []));
      setBitacoraIds(Array.from({ length: 12 }, () => null));
      setMeta({ etapa: null, aprendiz: null, matricula: null });

      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || "No se pudieron cargar las bitácoras.",
      });
    } finally {
      setLoading(false);
    }
  }, [puedeLeer, isMassive, etapaId, aprendizId, matriculaId, manejarErrorSesion]);

  useEffect(() => {
    getData();
  }, [getData]);

  /* =================== Actividades UI local =================== */
  const addActividad = (actividad) => {
    if (!puedeGestionarLocal) {
      Swal.fire("Sin permisos", "No tiene permisos para gestionar actividades de bitácoras.", "warning");
      return;
    }

    if (activeIdx === null) return;
    setBitacoras((prev) => {
      const copy = [...prev];
      copy[activeIdx] = [...copy[activeIdx], actividad];
      return copy;
    });
  };

  const clearBitacora = (idxB) => {
    if (!puedeEliminar) {
      Swal.fire("Sin permisos", "No tiene permisos para limpiar bitácoras.", "warning");
      return;
    }

    setBitacoras((prev) => {
      const copy = [...prev];
      copy[idxB] = [];
      return copy;
    });
  };

  /* =================== Persistencia individual =================== */
  const saveBitacora = async (numeroBitacora) => {
    if (isMassive) return;

    if (!puedeCrear) {
      Swal.fire("Sin permisos", "No tiene permisos para crear bitácoras.", "warning");
      return;
    }

    if (!etapaId || !aprendizId || !matriculaId) {
      Swal.fire({
        icon: "warning",
        title: "Faltan identificadores",
        text: "Verifica etapaId, aprendizId y matriculaId.",
      });
      return;
    }

    const rows = bitacoras[numeroBitacora - 1] || [];
    if (!rows.length) {
      Swal.fire({
        icon: "info",
        title: `Bitácora ${numeroBitacora}`,
        text: "No tiene actividades para guardar.",
      });
      return;
    }

    try {
      const payload = {
        etapaPracticaObjectId: etapaId,
        aprendizObjectId: aprendizId,
        matriculaObjectId: matriculaId,
        numeroBitacora: Number(numeroBitacora),
        actividadesBitacora: rows.map(mapActividadToPayload),
      };

      await axios.post("/api/bitacoras/add", payload, authConfig());

      await getData();
      Swal.fire({
        icon: "success",
        title: `Bitácora ${numeroBitacora} creada`,
        showConfirmButton: false,
        timer: 1400,
      });
    } catch (error) {
      if (manejarErrorSesion(error)) return;
      Swal.fire("Error", error.response?.data?.message || "No se pudo guardar.", "error");
    }
  };

  const updateBitacora = async (numeroBitacora) => {
    if (isMassive) return;

    if (!puedeActualizar) {
      Swal.fire("Sin permisos", "No tiene permisos para actualizar bitácoras.", "warning");
      return;
    }

    const bitId = bitacoraIds[numeroBitacora - 1];
    if (!bitId) {
      Swal.fire({
        icon: "warning",
        title: "No existe en BD",
        text: `La Bitácora ${numeroBitacora} no tiene _id para actualizar.`,
      });
      return;
    }

    const rows = bitacoras[numeroBitacora - 1] || [];
    if (!rows.length) {
      Swal.fire({
        icon: "info",
        title: `Bitácora ${numeroBitacora}`,
        text: "No tiene actividades para actualizar.",
      });
      return;
    }

    try {
      const payload = { actividadesBitacora: rows.map(mapActividadToPayload) };
      await axios.put(`/api/bitacoras/update/${bitId}`, payload, authConfig());

      await getData();
      Swal.fire({
        icon: "success",
        title: `Bitácora ${numeroBitacora} actualizada`,
        showConfirmButton: false,
        timer: 1400,
      });
    } catch (error) {
      if (manejarErrorSesion(error)) return;
      Swal.fire("Error", error.response?.data?.message || "No se pudo actualizar.", "error");
    }
  };

  /* =================== Excel =================== */
  const descargarExcelBitacora = async (numeroBitacora) => {
    if (!puedeLeer) {
      Swal.fire("Sin permisos", "No tiene permisos para consultar bitácoras.", "warning");
      return;
    }

    try {
      if (!Number.isInteger(numeroBitacora) || numeroBitacora < 1 || numeroBitacora > 12) {
        Swal.fire("Atención", "Número de bitácora inválido.", "info");
        return;
      }

      const bitId = bitacoraIds[numeroBitacora - 1];
      if (!bitId) {
        Swal.fire({
          icon: "info",
          title: `Bitácora ${numeroBitacora} aún no existe`,
          text: "Guárdala primero para poder generar el Excel.",
        });
        return;
      }

      if (!startDownload(`excel-${numeroBitacora}`)) return;

      const resp = await axios.get(`/api/bitacora/generar-excel/${bitId}`, {
        ...authConfig(),
        responseType: "blob",
      });

      const cd = resp.headers?.["content-disposition"] || "";
      const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
      const docAprendiz = meta?.aprendiz?.documentoAprendiz || "SIN_DOC";
      const fallbackName = `Bitacora_${numeroBitacora}_${docAprendiz}.xlsx`;
      const fileName = match ? decodeURIComponent(match[1]) : fallbackName;

      const blob = new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: "success",
        title: `Excel Bitácora ${numeroBitacora} generado`,
        showConfirmButton: false,
        timer: 1400,
      });
    } catch (error) {
      console.error("Error al generar Excel:", error);
      if (manejarErrorSesion(error)) return;
      Swal.fire(
        "Error",
        error.response?.data?.message || "No se pudo generar el Excel.",
        "error"
      );
    } finally {
      endDownload();
    }
  };

  /* =================== Descargar todos los excel =================== */
  const descargarTodosLosExcel = async () => {
    if (!puedeLeer) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para consultar bitácoras.",
        "warning"
      );
      return;
    }

    try {
      if (isDownloading) return;

      const existentes = bitacoraIds
        .map((id, idx) => ({ id, numero: idx + 1 }))
        .filter((x) => !!x.id);

      if (existentes.length === 0) {
        Swal.fire({
          icon: "info",
          title: "No hay bitácoras guardadas",
          text: "Primero guarda las bitácoras para poder generar los Excel.",
        });
        return;
      }

      const confirmar = await Swal.fire({
        icon: "question",
        title: "Descargar todas las bitácoras",
        html: `
        <div class="text-start">
          Se descargarán <b>${existentes.length}</b> archivo(s) Excel.<br/>
          Solo se descargarán las bitácoras que ya existen en la base de datos.
        </div>
      `,
        showCancelButton: true,
        confirmButtonText: "Sí, descargar",
        cancelButtonText: "Cancelar",
      });

      if (!confirmar.isConfirmed) return;

      if (!startDownload("todos")) return;

      for (const b of existentes) {
        const resp = await axios.get(`/api/bitacora/generar-excel/${b.id}`, {
          ...authConfig(),
          responseType: "blob",
        });

        const cd = resp.headers?.["content-disposition"] || "";
        const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);

        const docAprendiz = meta?.aprendiz?.documentoAprendiz || "SIN_DOC";
        const fallbackName = `Bitacora_${b.numero}_${docAprendiz}.xlsx`;
        const fileName = match ? decodeURIComponent(match[1]) : fallbackName;

        const blob = new Blob([resp.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);

        await new Promise((resolve) => setTimeout(resolve, 350));
      }

      Swal.fire({
        icon: "success",
        title: "Descarga completa",
        text: "Se descargaron las bitácoras disponibles.",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error descargando todas las bitácoras:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message ||
        "No se pudieron descargar todas las bitácoras.",
        "error"
      );
    } finally {
      endDownload();
    }
  };

  /* =================== Descargar Bitacoras Zip =================== */
  const descargarBitacorasZip = async () => {
    if (!puedeLeer) {
      Swal.fire("Sin permisos", "No tiene permisos para consultar bitácoras.", "warning");
      return;
    }

    try {
      if (isDownloading) return;

      const existentes = bitacoraIds.filter(Boolean);

      if (existentes.length === 0) {
        Swal.fire({
          icon: "info",
          title: "No hay bitácoras guardadas",
          text: "Primero guarda las bitácoras para poder generar el ZIP.",
        });
        return;
      }

      if (!startDownload("zip")) return;

      const resp = await axios.post(
        "/api/bitacora/generar-zip",
        {
          bitacoraIds: existentes,
        },
        {
          ...authConfig(),
          responseType: "blob",
        }
      );

      const blob = new Blob([resp.data], {
        type: "application/zip",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `BITACORAS_${meta?.aprendiz?.documentoAprendiz || "APRENDIZ"
        }.zip`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: "success",
        title: "ZIP generado",
        text: "Las bitácoras fueron descargadas correctamente.",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error descargando ZIP:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message ||
        "No se pudo descargar el archivo comprimido.",
        "error"
      );
    } finally {
      endDownload();
    }
  };

  /* =================== Bulk (Masivo) =================== */
  const bulkValidate = () => {
    if (!puedeCrear) {
      Swal.fire("Sin permisos", "No tiene permisos para crear bitácoras masivamente.", "warning");
      return false;
    }

    if (!matriculaId) {
      Swal.fire("Atención", "No se recibió el parámetro matriculaId.", "warning");
      return false;
    }
    const d = Number(bulkForm.numeroDesde) || 1;
    const h = Number(bulkForm.numeroHasta) || 12;
    if (d < 1 || d > 12 || h < 1 || h > 12 || d > h) {
      Swal.fire("Atención", "El rango debe estar entre 1 y 12 y 'desde' ≤ 'hasta'.", "info");
      return false;
    }
    return true;
  };

  const callBulk = async () => {
    if (!bulkValidate()) return;

    setBulkLoading(true);
    try {
      const activitiesByNumeroRaw = buildActivitiesByNumeroFromState();

      const activitiesByNumero = {};
      Object.keys(activitiesByNumeroRaw || {}).forEach((k) => {
        activitiesByNumero[k] = dedupeActs(activitiesByNumeroRaw[k]);
      });

      const body = {
        etapaPracticaObjectId: bulkForm.etapaPracticaObjectId || undefined,
        numeroDesde: Number(bulkForm.numeroDesde),
        numeroHasta: Number(bulkForm.numeroHasta),
        estadoBitacora: bulkForm.estadoBitacora,
        soloActivos: !!bulkForm.soloActivos,
        skipWhenNoEtapa: !!bulkForm.skipWhenNoEtapa,
        dryRun: false,
        mergeMode: bulkForm.mergeMode || "replace",
        activitiesDefault: [],
        activitiesByNumero,
      };

      const { data } = await axios.post(
        `/api/bitacoras/bulk/${matriculaId}`,
        body,
        authConfig()
      );

      Swal.fire({
        icon: "success",
        title: "Masivo completado",
        html: `
          <div class="text-start">
            <div><b>${data?.message || "Bitácoras procesadas correctamente."}</b></div>
            <div class="mt-2 small text-muted">
              Revisa el resumen en tu backend si lo estás retornando.
            </div>
          </div>
        `,
      });
    } catch (error) {
      if (manejarErrorSesion(error)) return;
      const msg = error?.response?.data?.message || error.message || "Error en el masivo.";
      Swal.fire("Error", msg, "error");
    } finally {
      setBulkLoading(false);
    }
  };

  /* =================== Filtro tarjetas =================== */
  const filtered = useMemo(() => {
    return bitacoras
      .map((rows, i) => ({ rows, index: i }))
      .filter(({ rows, index }) => {
        const matches = `bitacora ${index + 1}`.toLowerCase().includes(query.toLowerCase());
        const passData = onlyWithData ? rows.length > 0 : true;
        return matches && passData;
      });
  }, [bitacoras, query, onlyWithData]);

  if (!puedeLeer) {
    return (
      <div className="container-fluid py-4 bg-light">
        <div className="container-fluid px-2 px-md-4">
          <div className="bg-white border rounded-4 shadow-sm p-4">
            <div className="text-center text-secondary py-5">
              <div className="display-6 mb-3">
                <i className="fa-solid fa-lock"></i>
              </div>

              <h5 className="fw-semibold text-dark">Sin permiso de lectura</h5>

              <p className="mb-4">No tienes permisos para visualizar las bitácoras.</p>

              <button
                className="btn btn-outline-dark"
                type="button"
                onClick={() => navigate(-1)}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* =================== Render =================== */
  return (
    <form ref={formRef} >
      {/* Encabezado contextual con meta */}
      {!loading && !isMassive && (
        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-user-graduate"></i>
                  </div>
                  <div className="w-100">
                    <div className="fw-semibold">{nombreAprendiz()}</div>
                    <div className="small text-muted mt-1">
                      Documento: {safe(meta?.aprendiz?.documentoAprendiz)} · Tel:{" "}
                      {safe(meta?.aprendiz?.telefono1Aprendiz)} · Email:{" "}
                      {safe(meta?.aprendiz?.emailAprendiz)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-regular fa-calendar-check"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Fecha Etapa Productiva</div>
                    <div className="fw-semibold">
                      {fmtDate(etapaFechaInicio)} — {fmtDate(etapaFechaFin)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-info-subtle text-info d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-book"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Programa</div>
                    <div className="fw-semibold">{safe(programa?.nombrePrograma)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-warning-subtle text-warning d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-people-group"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Grupo</div>
                    <div className="fw-semibold">
                      {safe(grupo?.codigoGrupo, meta?.matricula?.fichaGrupo ?? "—")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-secondary-subtle text-secondary d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-building"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Ente Conformador</div>
                    <div className="fw-semibold">{safe(nombreEnte)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-chalkboard-user"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Instructor de Seguimiento</div>
                    <div className="fw-semibold">{safe(instructorNombre)}</div>
                    <div className="small text-muted mt-1">
                      Doc: {safe(instructor?.documentoInstructor)} · Tel:{" "}
                      {safe(instructor?.telefono1Instructor ?? instructor?.telefonoInstructor)} ·
                      Email: {safe(instructor?.emailInstructor)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-dark-subtle text-dark d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-user-tie"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Jefe Inmediato</div>
                    <div className="fw-semibold">{safe(jefeNombre)}</div>
                    <div className="small text-muted mt-1">
                      Doc: {safe(jefe?.documentoJefe)} · Tel:{" "}
                      {safe(jefe?.telefono1Jefe ?? meta?.etapa?.enteObjectId?.jefeTelefono)} ·
                      Email: {safe(jefe?.emailJefe ?? meta?.etapa?.enteObjectId?.jefeCorreo)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header principal + toolbar */}
      <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mb-4">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <h3 className="fw-bold mb-1">{tituloPagina}</h3>
            <div className="text-muted small">
              {isMassive
                ? "Crea/actualiza bitácoras de forma masiva usando tu plantilla local."
                : "Agrega actividades, guarda o actualiza cada bitácora."}
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate(-1)}
              disabled={loading || bulkLoading || isDownloading}
            >
              <i className="fa-solid fa-arrow-rotate-left me-2"></i>
              Volver
            </button>

            {!isMassive && (
              <>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={descargarTodosLosExcel}
                  disabled={loading || isDownloading}
                >
                  <i
                    className={`fa-solid ${downloadAction === "todos" ? "fa-spinner fa-spin" : "fa-file-excel"
                      } me-2`}
                  ></i>
                  Descargar todas
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={descargarBitacorasZip}
                  disabled={loading || isDownloading}
                >
                  <i
                    className={`fa-solid ${downloadAction === "zip" ? "fa-spinner fa-spin" : "fa-file-zipper"
                      } me-2`}
                  ></i>
                  Descargar ZIP
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Panel MASIVO */}
      {isMassive && (

        <div className="border rounded-4 bg-light p-3 shadow-sm mb-3">
          <div className="row g-2 align-items-end">

            <div className="col-6 col-lg-2">
              <label className="form-label small fw-bold text-uppercase mb-1">
                Desde
              </label>

              <input
                type="number"
                className="form-control"
                style={{ height: "44px" }}
                min={1}
                max={12}
                value={bulkForm.numeroDesde}
                onChange={(e) => bulkSet("numeroDesde", e.target.value)}
                disabled={!puedeCrear || bulkLoading}
              />
            </div>

            <div className="col-6 col-lg-2">
              <label className="form-label small fw-bold text-uppercase mb-1">
                Hasta
              </label>

              <input
                type="number"
                className="form-control"
                style={{ height: "44px" }}
                min={1}
                max={12}
                value={bulkForm.numeroHasta}
                onChange={(e) => bulkSet("numeroHasta", e.target.value)}
                disabled={!puedeCrear || bulkLoading}
              />
            </div>

            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label small fw-bold text-uppercase mb-1">
                Estado
              </label>

              <select
                className="form-select"
                style={{ height: "44px" }}
                value={bulkForm.estadoBitacora}
                onChange={(e) => bulkSet("estadoBitacora", e.target.value)}
                disabled={!puedeCrear || bulkLoading}
              >
                <option value="BORRADOR">BORRADOR</option>
                <option value="ENVIADA">ENVIADA</option>
                <option value="REVISION">REVISION</option>
                <option value="APROBADA">APROBADA</option>
                <option value="RECHAZADA">RECHAZADA</option>
                <option value="FINALIZADA">FINALIZADA</option>
              </select>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="form-label small fw-bold text-uppercase mb-1">
                Condiciones
              </label>

              <div className="d-flex gap-2">
                <label
                  className="border rounded-3 bg-white px-2 flex-fill text-center small fw-semibold mb-0 d-flex align-items-center justify-content-center"
                  style={{ height: "44px", cursor: "pointer" }}
                >
                  <input
                    className="form-check-input me-2"
                    type="checkbox"
                    checked={bulkForm.soloActivos}
                    onChange={(e) => bulkSet("soloActivos", e.target.checked)}
                    disabled={!puedeCrear || bulkLoading}
                  />
                  Activos
                </label>

                <label
                  className="border rounded-3 bg-white px-2 flex-fill text-center small fw-semibold mb-0 d-flex align-items-center justify-content-center"
                  style={{ height: "44px", cursor: "pointer" }}
                >
                  <input
                    className="form-check-input me-2"
                    type="checkbox"
                    checked={bulkForm.skipWhenNoEtapa}
                    onChange={(e) => bulkSet("skipWhenNoEtapa", e.target.checked)}
                    disabled={!puedeCrear || bulkLoading}
                  />
                  Sin etapa
                </label>
              </div>
            </div>

            <div className="col-12 col-lg-3">
              <label className="form-label small fw-bold text-uppercase mb-1">
                Acción
              </label>

              {puedeCrear && (
                <button
                  type="button"
                  className="btn btn-primary w-100 fw-semibold"
                  style={{ height: "44px" }}
                  disabled={bulkLoading}
                  onClick={callBulk}
                >
                  <i
                    className={`fa-solid ${bulkLoading ? "fa-spinner fa-spin" : "fa-cloud-arrow-up"
                      } me-2`}
                  />
                  Guardar Masivo
                </button>
              )}
            </div>

          </div>
        </div>

      )}

      {/* Grid 12 tarjetas */}
      <div className="row g-4">
        {filtered.map(({ rows, index: idxB }) => {
          const numero = idxB + 1;
          const existe = Boolean(bitacoraIds[idxB]);
          const canPersist = !isMassive;

          return (
            <div className="col-12 col-md-6 col-lg-3" key={`bitacora-${idxB}`}>
              <div className="card h-100 shadow-sm border-0">
                <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center"
                      style={{ width: 36, height: 36 }}
                    >
                      <i className="fa-regular fa-note-sticky"></i>
                    </div>

                    <div className="d-flex align-items-center">
                      <div className="fw-semibold">Bitácora {numero}</div>
                      {existe && !isMassive && (
                        <span className="badge text-bg-light ms-2">EXISTENTE</span>
                      )}
                    </div>
                  </div>

                  <div className="dropdown">
                    <button
                      className="btn btn-sm btn-outline-secondary dropdown-toggle"
                      data-bs-toggle="dropdown"
                      type="button"
                    >
                      Acciones
                    </button>

                    <ul className="dropdown-menu dropdown-menu-end">
                      <li>
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={() => {
                            setActiveIdx(idxB);
                            setShowTable(true);
                          }}
                        >
                          <i className="fa-solid fa-table me-2"></i> Ver detalles (tabla)
                        </button>
                      </li>

                      {puedeGestionarLocal && (
                        <li>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => {
                              setActiveIdx(idxB);
                              setShowForm(true);
                            }}
                          >
                            <i className="fa-solid fa-plus me-2"></i> Añadir actividad
                          </button>
                        </li>
                      )}

                      <li>
                        <hr className="dropdown-divider" />
                      </li>

                      {puedeEliminar && (
                        <li>
                          <button
                            type="button"
                            className="dropdown-item text-danger"
                            onClick={() => clearBitacora(idxB)}
                          >
                            <i className="fa-solid fa-eraser me-2"></i> Limpiar bitácora
                          </button>
                        </li>
                      )}

                      {!isMassive && (
                        <li>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => descargarExcelBitacora(numero)}
                            disabled={isDownloading}
                          >
                            <i className={`fa-solid ${downloadAction === "excel-" + numero ? "fa-spinner fa-spin" : "fa-file-excel"} me-2`}></i> Exportar a Excel
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>

                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="small text-muted">Los detalles se ven en “Detalles”.</div>
                    <Pill variant={rows.length ? "success" : "secondary"}>
                      {rows.length} registro{rows.length !== 1 ? "s" : ""}
                    </Pill>
                  </div>

                  <div className="progress mt-3" style={{ height: 6 }}>
                    <div
                      className={`progress-bar ${rows.length ? "bg-success" : "bg-secondary"}`}
                      style={{ width: `${Math.min(rows.length * 10, 100)}%` }}
                      role="progressbar"
                      aria-valuenow={rows.length}
                      aria-valuemin="0"
                      aria-valuemax="10"
                    />
                  </div>

                  {canPersist && (
                    <div className="mt-3 small text-muted">
                      {existe ? "Puedes actualizar en BD." : "Aún no existe en BD (Guardar)."}
                    </div>
                  )}
                </div>

                <div className="card-footer bg-white d-flex justify-content-between align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setActiveIdx(idxB);
                      setShowTable(true);
                    }}
                  >
                    <i className="fa-solid fa-table me-1"></i> Detalles
                  </button>

                  <div className="d-flex gap-2">
                    {puedeGestionarLocal && (
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => {
                          setActiveIdx(idxB);
                          setShowForm(true);
                        }}
                      >
                        <i className="fa-solid fa-plus me-1"></i> Añadir
                      </button>
                    )}

                    {canPersist && existe && puedeActualizar && (
                      <button
                        type="button"
                        className="btn btn-dark btn-sm"
                        onClick={() => updateBitacora(numero)}
                        title="Actualizar en BD"
                      >
                        <i className="fa-solid fa-floppy-disk me-1"></i> Actualizar
                      </button>
                    )}

                    {canPersist && !existe && puedeCrear && (
                      <button
                        type="button"
                        className="btn btn-dark btn-sm"
                        onClick={() => saveBitacora(numero)}
                        title="Guardar en BD"
                      >
                        <i className="fa-solid fa-floppy-disk me-1"></i> Guardar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="col-12">
            <EmptyState
              title="Sin coincidencias"
              subtitle="Intenta con otro término o desmarca 'Sólo con actividades'."
            />
          </div>
        )}
      </div>

      {/* Modal Tabla */}
      {showTable && activeIdx !== null && (
        <DetalleTableModal
          show={showTable}
          onClose={() => {
            setShowTable(false);
            setActiveIdx(null);
          }}
          actividades={bitacoras[activeIdx]}
          puedeEliminar={puedeEliminar}
          onDelete={(i) => {
            if (!puedeEliminar) {
              Swal.fire("Sin permisos", "No tiene permisos para eliminar actividades.", "warning");
              return;
            }

            setBitacoras((prev) => {
              const copy = [...prev];
              const rows = [...copy[activeIdx]];
              rows.splice(i, 1);
              copy[activeIdx] = rows;
              return copy;
            });
          }}
          bitacoraIndex={activeIdx}
        />
      )}

      {/* Modal Form */}
      {showForm && activeIdx !== null && puedeGestionarLocal && (
        <ActividadFormModal
          show={showForm}
          onClose={() => {
            setShowForm(false);
            setActiveIdx(null);
          }}
          onSave={addActividad}
        />
      )}
    </form>
  );
};

export default RegistrarBitacoras;
